import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Plus, Shield, Pencil, Trash2, Users as UsersIcon, Search, Link2 } from 'lucide-react';
import companyAdminService from '../../services/companyAdminService';
import PermissionCheckboxGroup from '../PlatformAdmin/PermissionCheckboxGroup';
import PaginationBar from '../PlatformAdmin/PaginationBar';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { PERMISSIONS } from '../../constants/permissions';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import { platformMcpTooltips, platformMcpUntickWarning } from '../../utils/platformMcpPermission';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const PAGE_SIZE = 50;
const emptyForm = { id: null, name: '', description: '', permission_keys: [] };
const ROLE_KEY_TOOLTIPS = platformMcpTooltips('role');

/**
 * Company Admin role builder (spec §5.2). Custom roles only; system roles are
 * shown read-only. Permission checkboxes come from /company-admin/permissions,
 * already filtered to the company's envelope — the exact set this admin may grant.
 */
const CompanyRolesPanel = ({ notify, catalog }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [dialog, setDialog] = useState(null); // { mode }
  const [form, setForm] = useState(emptyForm);
  // The role's keys as saved, so unticking Platform MCP can warn before saving.
  const [savedKeys, setSavedKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyAdminService.listRoles({
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      const list = Array.isArray(data) ? data : data?.roles || [];
      setRoles(list);
      setTotal(Number.isFinite(data?.total) ? data.total : list.length);
    } catch (err) {
      setError(err?.message || 'Failed to load roles.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setSavedKeys([]);
    setDialog({ mode: 'create' });
  };

  const openEdit = (role) => {
    setForm({
      id: role.id,
      name: role.name,
      description: role.description || '',
      permission_keys: role.permission_keys || [],
    });
    setSavedKeys(role.permission_keys || []);
    setDialog({ mode: 'edit' });
  };

  const togglePerm = (key) => {
    setForm((prev) => ({
      ...prev,
      permission_keys: prev.permission_keys.includes(key)
        ? prev.permission_keys.filter((k) => k !== key)
        : [...prev.permission_keys, key],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        permission_keys: form.permission_keys,
      };
      if (dialog?.mode === 'edit') {
        await companyAdminService.updateRole(form.id, payload);
        notify('Role updated.', 'success');
      } else {
        await companyAdminService.createRole(payload);
        notify('Role created.', 'success');
      }
      setDialog(null);
      await load();
    } catch (err) {
      // Surfaces envelope violations (offending keys) and duplicate-name errors.
      notify(err?.data?.detail || err?.message || 'Failed to save role.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm?.role) return;
    try {
      setBusy(true);
      const res = await companyAdminService.deleteRole(confirm.role.id);
      const affected = res?.affected_users ?? 0;
      notify(`Role deleted.${affected ? ` ${affected} user(s) lost this role.` : ''}`, 'info');
      setConfirm(null);
      await load();
    } catch (err) {
      notify(err?.data?.detail || err?.message || 'Failed to delete role.', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Company admins can't edit the default member role, so access comes from a
  // custom role — only worth saying once the company has the key.
  const offersPlatformMcp = (catalog || []).some((p) => p.key === PERMISSIONS.PLATFORM_MCP);

  return (
    <>
      {offersPlatformMcp && (
        <Box className={styles.noteBanner}>
          <Link2 size={18} />
          <span>{PLATFORM_MCP_COPY.rolesHint}</span>
        </Box>
      )}
      <Box className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Search roles…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<Plus size={18} />} className={styles.primaryBtn} onClick={openCreate}>
          New role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {roles.map((role) => (
            <Box key={role.id} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
              <Box className={styles.companyMain} sx={{ flex: '1 1 240px' }}>
                <span className={styles.companyAvatar}><Shield size={18} /></span>
                <Box>
                  <span className={styles.companyName}>{role.name}</span>
                  <span className={styles.companyMeta}>{role.description || '—'}</span>
                </Box>
              </Box>

              <span className={`${styles.badge} ${role.is_system ? styles.badgeSystem : ''}`}>
                {role.is_system ? 'System' : 'Custom'}
              </span>
              <span className={styles.badge}>{(role.permission_keys || []).length} areas</span>

              <Box className={styles.actionsCell}>
                {!role.is_system ? (
                  <>
                    <Tooltip title="Edit">
                      <IconButton size="small" className={styles.subtleBtn} onClick={() => openEdit(role)}>
                        <Pencil size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" className={styles.dangerIcon} onClick={() => setConfirm({ role })}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <span className={styles.muted} style={{ padding: '0 8px' }}>Read-only</span>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="roles"
          onOffsetChange={setOffset}
        />
      )}

      {/* Create / edit */}
      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} classes={{ paper: styles.dialogPaper }} maxWidth="md" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          {dialog?.mode === 'edit' ? `Edit role — ${form.name}` : 'New role'}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus label="Role name" fullWidth variant="outlined"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={styles.formField}
          />
          <TextField
            label="Description" fullWidth variant="outlined"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className={styles.formField}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Permissions
            </Typography>
            <PermissionCheckboxGroup
              catalog={catalog}
              selected={form.permission_keys}
              onToggle={togglePerm}
              keyTooltips={ROLE_KEY_TOOLTIPS}
              keyWarnings={platformMcpUntickWarning(savedKeys, form.permission_keys)}
            />
          </Box>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setDialog(null)} disabled={saving} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()} className={styles.primaryBtn}>
            {saving ? <CircularProgress size={20} color="inherit" /> : dialog?.mode === 'edit' ? 'Save changes' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} classes={{ paper: styles.dialogPaper }} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Delete role</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}><UsersIcon /></Box>
          <Typography className={styles.deleteHeading}>Delete "{confirm?.role?.name}"?</Typography>
          <Typography className={styles.deleteText}>
            Any users holding this role will lose it immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setConfirm(null)} disabled={busy} className={styles.cancelButton}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} disabled={busy} className={styles.dangerButton}>
            {busy ? 'Working...' : 'Delete role'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyRolesPanel;
